import { styled } from '@linaria/react';
import { Loader, Stack } from '@mantine/core';
import { useMemo } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  useGoalProgressChartData,
  useOutcomeProgressChartData,
} from '../../../../api/strategy-client/strategy-client.hooks';
import { useTranslation } from 'react-i18next';
import { secondaryBase, skyDark, skyLight, skyTight } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip as InfoTooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { CHART_COLORS } from './color-helpers';

type ProgressOverTimeChartProps = {
  outcomeId?: string | undefined;
  goalId?: string | undefined;
};

export function ProgressOverTimeChart({ outcomeId, goalId }: ProgressOverTimeChartProps) {
  const i18n = useTranslation();
  // Fetch aggregated chart data with top 6 initiatives + Others
  const { data: outcomeChartData, query: outcomeQuery } = useOutcomeProgressChartData(outcomeId, 6);
  const { data: goalChartData, query: goalQuery } = useGoalProgressChartData(goalId, 6);

  const chartDataResponse = outcomeId ? outcomeChartData : goalChartData;
  const query = outcomeId ? outcomeQuery : goalQuery;

  const chartData = useMemo(() => {
    if (!chartDataResponse || !chartDataResponse.months || !chartDataResponse.initiatives) {
      return [];
    }

    // Transform API response into chart format
    return chartDataResponse.months.map((month: string) => {
      const dataPoint: Record<string, string | number> = { month };

      chartDataResponse.initiatives.forEach((initiative: any, index: number) => {
        const percentage = initiative.data[month] || 0;
        dataPoint[`initiative_${index}`] = percentage;
      });

      return dataPoint;
    });
  }, [chartDataResponse]);

  const initiatives = chartDataResponse?.initiatives || [];

  if (!outcomeId && !goalId) {
    return (
      <SectionCard>
        <Stack gap={12}>
          <SectionHeader>
            <Icon name="trending_up" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
            <SectionTitle>{i18n.t('strategy.overview.sections.progress_over_time')}</SectionTitle>
          </SectionHeader>
          <EmptyState>
            <Icon name="info-circle" size={16} color={skyDark} />
            <Text size="small" color={skyDark}>
              {i18n.t('strategy.overview.empty_states.no_data')}
            </Text>
          </EmptyState>
        </Stack>
      </SectionCard>
    );
  }

  if (query.isLoading) {
    return (
      <SectionCard>
        <Stack gap={12}>
          <SectionHeader>
            <Icon name="trending_up" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
            <SectionTitle>{i18n.t('strategy.overview.sections.progress_over_time')}</SectionTitle>
            <Loader size="sm" variant="dots" color={secondaryBase} />
          </SectionHeader>
          <ChartContainer />
        </Stack>
      </SectionCard>
    );
  }

  if (chartData.length === 0) {
    return (
      <SectionCard>
        <Stack gap={12}>
          <SectionHeader>
            <Icon name="trending_up" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
            <SectionTitle>{i18n.t('strategy.overview.sections.progress_over_time')}</SectionTitle>
          </SectionHeader>
          <EmptyState>
            <Icon name="info-circle" size={16} color={skyDark} />
            <Text size="small" color={skyDark}>
              {i18n.t('strategy.overview.empty_states.no_data')}
            </Text>
          </EmptyState>
        </Stack>
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      <Stack gap={12}>
        <SectionHeader>
          <Icon name="trending_up" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
          <SectionTitle>{i18n.t('strategy.overview.sections.progress_over_time')}</SectionTitle>
          <InfoTooltip variant="black" label={i18n.t('strategy.overview.tooltips.progress_over_time')} zIndex={1000}>
            <Icon name="info" size={16} color={skyDark} style={{ cursor: 'help' }} />
          </InfoTooltip>
        </SectionHeader>

        <ChartContainer>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
              <XAxis
                dataKey="month"
                stroke={skyDark}
                style={{ fontSize: '12px', fontFamily: 'Figtree' }}
                label={{
                  value: i18n.t('strategy.overview.charts.month'),
                  position: 'insideBottom',
                  offset: -15,
                  style: { fontSize: '12px', fill: skyDark },
                }}
                height={70}
              />
              <YAxis
                stroke={skyDark}
                style={{ fontSize: '12px', fontFamily: 'Figtree' }}
                label={{
                  value: i18n.t('strategy.overview.charts.completion_percentage'),
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: '12px', fill: skyDark },
                }}
                domain={[0, 100]}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || payload.length === 0) return null;

                  return (
                    <div
                      style={{
                        backgroundColor: 'white',
                        border: `1px solid ${skyLight}`,
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '12px',
                        fontFamily: 'Figtree',
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{payload[0].payload.month}</div>
                      {payload.map((entry: any, index: number) => {
                        const initiativeIndex = Number.parseInt((entry.dataKey ?? '').replace('initiative_', ''), 10);
                        const initiative = initiatives[initiativeIndex];
                        const isOthers = initiative?.id === 'others';

                        return (
                          <div key={index} style={{ marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  backgroundColor: entry.color,
                                  borderRadius: '2px',
                                }}
                              />
                              <span style={{ fontWeight: 'medium' }}>{initiative?.name || 'Unknown'}:</span>
                              <span style={{ fontWeight: 'bold' }}>{entry.value ?? 0}%</span>
                            </div>
                            {isOthers &&
                              initiative.included_initiatives &&
                              initiative.included_initiatives.length > 0 && (
                                <div style={{ marginLeft: '20px', marginTop: '4px', fontSize: '11px', color: skyDark }}>
                                  <div style={{ fontStyle: 'italic', marginBottom: '2px' }}>Includes:</div>
                                  {initiative.included_initiatives.map((inc: any) => (
                                    <div key={inc.id}>• {inc.name}</div>
                                  ))}
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '12px', fontFamily: 'Figtree', paddingTop: '20px' }}
                formatter={(value: string) => {
                  const index = Number.parseInt(value.replace('initiative_', ''), 10);
                  return initiatives[index]?.name || value;
                }}
              />
              {initiatives.map((initiative: any, index: number) => (
                <Line
                  key={initiative.id}
                  type="monotone"
                  dataKey={`initiative_${index}`}
                  stroke={CHART_COLORS[index % CHART_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </Stack>
    </SectionCard>
  );
}

const SectionCard = styled.div`
  background-color: ${skyTight};
  border-radius: 16px;
  padding: 20px;
  border: 1px solid ${skyLight};
  flex: 1;
  min-height: 350px;
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
`;

const SectionTitle = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  text-align: left;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  margin-top: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  border: 1px dashed ${skyLight};
`;
